import { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Header,
  HeaderGlobalBar,
  HeaderGlobalAction,
} from '@carbon/react';
import {
  Dashboard,
  List,
  SendAlt,
  Building,
  DataConnected,
  FlowData,
  Events,
  User as IconUser,
  Logout,
  Sun,
  Moon,
} from '@carbon/icons-react';
import { Permission, ROLE_LABELS, User } from '../lib/api';
import { useTheme } from '../lib/theme';
import crestLogo from '../assets/brand/favicon.ico';

const NAV = [
  { to: '/', label: 'Overview', icon: Dashboard, end: true },
  { to: '/queue', label: 'Queue', icon: List },
  { to: '/submissions', label: 'Submissions', icon: SendAlt },
  { to: '/facilities', label: 'Facilities', icon: Building },
  { to: '/targets', label: 'DHIS2 servers', icon: DataConnected },
  { to: '/mappings', label: 'Mappings', icon: FlowData },
  { to: '/audit', label: 'Activity log', icon: Events },
];

export function Shell({
  user,
  can,
  onSignOut,
  eyebrow,
  title,
  subtitle,
  icon: PageIcon,
  actions,
  children,
}: {
  user: User;
  can: (permission: Permission) => boolean;
  onSignOut: () => void;
  eyebrow?: string;
  title: string;
  subtitle?: ReactNode;
  icon?: React.ComponentType<{ size?: number | string; className?: string }>;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="shell">
      <Header aria-label="SSEMR Interoperability Hub" className="hub-header">
        <div className="hub-header-brand">
          <img src={crestLogo} alt="" className="hub-header-crest" />
          <div className="hub-header-titles">
            <span className="hub-header-title">SSEMR Hub</span>
            <span className="hub-header-subtitle">Interoperability</span>
          </div>
        </div>

        <HeaderGlobalBar className="hub-header-global">
          <div className="hub-header-user">
            <span className="hub-user-name">{user.name}</span>
            <span className="hub-user-role">{ROLE_LABELS[user.role] ?? user.role}</span>
          </div>
          <HeaderGlobalAction
            aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            onClick={toggleTheme}
            className="hub-theme-toggle"
            tooltipAlignment="end"
          >
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
          </HeaderGlobalAction>
        </HeaderGlobalBar>
      </Header>

      <div className="shell-body">
        <aside className="sidebar" aria-label="Application Navigation">
          <div className="nav-group">
            {NAV.map((item) => {
              const IconComponent = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                >
                  <IconComponent className="nav-icon" size={16} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}

            {can('users.read') ? (
              <NavLink
                to="/users"
                className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              >
                <IconUser className="nav-icon" size={16} />
                <span>Users</span>
              </NavLink>
            ) : null}
          </div>

          <div className="nav-spacer" />

          <button
            className="nav-link nav-signout"
            onClick={() => {
              onSignOut();
              navigate('/login');
            }}
          >
            <Logout className="nav-icon" size={16} />
            <span>Sign out</span>
          </button>
        </aside>

        <div className="main">
          <header className="topbar">
            <div className="page-header-content">
              {PageIcon ? (
                <div className="page-header-icon" aria-hidden="true">
                  <PageIcon size={28} />
                </div>
              ) : null}
              <div className="page-header-text">
                {eyebrow ? <div className="page-header-eyebrow">{eyebrow}</div> : null}
                <h1 className="page-title">{title}</h1>
                {subtitle ? <div className="page-sub">{subtitle}</div> : null}
              </div>
            </div>
            {actions ? <div className="row">{actions}</div> : null}
          </header>
          <main className="content">{children}</main>
        </div>
      </div>
    </div>
  );
}
